import { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { memoize } from 'micro-memoize';

import { CurrentUserCheck } from '@futrli/shared-services/authenticator/auth0/types';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import {
  ConsolidatedForecastCreateData,
  ConsolidatedForecastUpdateData
} from '../../../../predict/src/core/api/types/consolidated-forecasting';
import {
  createConsolidatedForecastMutation,
  deleteConsolidatedForecastMutation,
  updateConsolidatedForecastMutation
} from '../../../mutations/consolidatedForecasts';
import { consolidatedForecastsQuery } from '../../../queries/consolidatedForecasts';
import { currentUserQuery } from '../../../queries/currentUser';
import {
  ExtendedConsolidatedForecastListItem,
  processConsolidatedForecasts
} from '../../../processors/forecasts/consolidatedForecastsList';
import { membersQuery } from '../../../queries/members';

export interface ConsolidatedForecastsListViewState extends ApiViewState {
  currentUser?: CurrentUserCheck;
  errorCreating: Error | null;
  errorDeleting: Error | null;
  errorUpdating: Error | null;
  forecasts?: ExtendedConsolidatedForecastListItem[];
  isCreating: boolean;
  isDeleting: boolean;
  isUpdating: boolean;
  newForecastId?: string;
}

export class ConsolidatedForecastsListView extends ApiView {
  private currentUserObs: ReturnType<typeof currentUserQuery>;
  private forecastsObs: ReturnType<typeof consolidatedForecastsQuery>;
  private membersObs: ReturnType<typeof membersQuery>;

  private createMutation: ReturnType<typeof createConsolidatedForecastMutation>;
  private deleteMutation: ReturnType<typeof deleteConsolidatedForecastMutation>;
  private updateMutation: ReturnType<typeof updateConsolidatedForecastMutation>;

  private memoizedProcessForecasts: typeof processConsolidatedForecasts;

  public state: ConsolidatedForecastsListViewState = {
    ...getDefaultApiViewState(),
    currentUser: undefined,
    errorCreating: null,
    errorDeleting: null,
    errorUpdating: null,
    forecasts: [],
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    newForecastId: undefined
  };

  constructor(private workspaceId: string, private consolidationId: string, private flags: LDFlagSet = {}) {
    super();

    // Observers
    this.currentUserObs = this.addQuery(currentUserQuery());
    this.forecastsObs = this.addQuery(consolidatedForecastsQuery(workspaceId, consolidationId));
    this.membersObs = this.addQuery(membersQuery(workspaceId, consolidationId, true));

    // Mutations
    this.createMutation = this.addMutation(createConsolidatedForecastMutation(workspaceId, consolidationId));
    this.deleteMutation = this.addMutation(deleteConsolidatedForecastMutation(workspaceId, consolidationId));
    this.updateMutation = this.addMutation(updateConsolidatedForecastMutation(workspaceId, consolidationId));

    this.memoizedProcessForecasts = memoize(processConsolidatedForecasts);

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************
  public createConsolidatedForecast(data: ConsolidatedForecastCreateData) {
    return this.createMutation.mutate(data);
  }

  public deleteConsolidatedForecast(forecastId: string) {
    return this.deleteMutation.mutate(forecastId);
  }

  public publishConsolidatedForecast(forecastId: string) {
    return this.updateMutation.mutate({ forecastId, data: { status: 'published' } });
  }

  public resetMutations() {
    this.createMutation.reset();
    this.deleteMutation.reset();
    this.updateMutation.reset();
    this.updateState();
  }

  public unpublishConsolidatedForecast(forecastId: string) {
    return this.updateMutation.mutate({ forecastId, data: { status: 'draft' } });
  }

  public updateConsolidatedForecast(forecastId: string, data: ConsolidatedForecastUpdateData) {
    return this.updateMutation.mutate({ forecastId, data });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************
  private get currentUser() {
    return this.getResult(this.currentUserObs).data;
  }

  private get errorCreating() {
    return this.createMutation.getCurrentResult().error;
  }

  private get errorDeleting() {
    return this.deleteMutation.getCurrentResult().error;
  }

  private get errorUpdating() {
    return this.updateMutation.getCurrentResult().error;
  }

  private get forecasts() {
    const rawForecasts = this.getResult(this.forecastsObs).data ?? [];
    const currentUser = this.currentUser;
    const members = this.members;
    if (!currentUser || !rawForecasts || !members) {
      return undefined;
    }
    return this.memoizedProcessForecasts(rawForecasts, currentUser, members, this.flags);
  }

  private get members() {
    return this.getResult(this.membersObs).data;
  }

  protected getNewState(): ConsolidatedForecastsListViewState {
    return {
      ...super.getNewState(),
      currentUser: this.currentUser,
      errorCreating: this.errorCreating,
      errorDeleting: this.errorDeleting,
      errorUpdating: this.errorUpdating,
      forecasts: this.forecasts,
      isCreating: this.isCreating,
      isDeleting: this.isDeleting,
      isUpdating: this.isUpdating,
      newForecastId: this.newForecastId
    };
  }

  private get isCreating() {
    return this.createMutation.getCurrentResult().status === 'pending';
  }

  private get isDeleting() {
    return this.deleteMutation.getCurrentResult().status === 'pending';
  }

  private get isUpdating() {
    return this.updateMutation.getCurrentResult().status === 'pending';
  }

  private get newForecastId() {
    return this.createMutation.getCurrentResult().data?.id;
  }
}
